import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { X } from "lucide-react";

const AssignNurseModal = ({ nurse, onClose, onSuccess }) => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res = await axiosInstance.get("/nurse-assignments/doctors");
      setDoctors(res.data.doctors || []);
    } catch (error) {
      console.error("Failed to fetch doctors:", error);
      setError("Failed to load doctors. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDoctorId) {
      setError("Please select a doctor");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axiosInstance.post("/nurse-assignments", {
        nurseId: nurse.id,
        doctorId: parseInt(selectedDoctorId),
      });

      onSuccess();
    } catch (error) {
      console.error("Failed to assign nurse:", error);
      setError(
        error.response?.data?.message ||
          "Failed to assign nurse. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter out doctors already assigned to this nurse
  const assignedDoctorIds = nurse.assignments?.map((a) => a.doctor?.id) || [];
  const availableDoctors = doctors.filter(
    (doctor) => !assignedDoctorIds.includes(doctor.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Assign Nurse to Doctor
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Nurse Info */}
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800">Nurse:</p>
            <p className="text-lg font-semibold text-blue-900">{nurse.name}</p>
          </div>

          {/* Doctor Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Doctor
            </label>
            <select
              value={selectedDoctorId}
              onChange={(e) => {
                setSelectedDoctorId(e.target.value);
                setError("");
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Select a doctor --</option>
              {availableDoctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  Dr. {doctor.name}
                </option>
              ))}
            </select>
            {availableDoctors.length === 0 && (
              <p className="mt-2 text-sm text-gray-500 italic">
                All doctors in your branch are already assigned to this nurse.
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || availableDoctors.length === 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? "Assigning..." : "Assign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignNurseModal;
